import { ReactElement, useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  LinearProgress,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { useProjectStore } from "../../stores/project.store";
import { useAuth } from "../../contexts/auth.context";
import projectsService, { File } from "../../services/projects.service";
import ConfirmationForm from "./ConfirmationForm";
import BaseModal from "../modals/BaseModal";

export interface FileUploadFormProps {
  onSubmit?: (uploadedFiles: string[]) => void;
  onCancel?: () => void;
  selectedFiles?: string[];
  onRunSelectedFiles?: (selectedFileNames: string[]) => void;
  onFilesChanged?: () => void;
  files?: File[];
}

const FileUploadForm = ({
  onSubmit,
  onCancel,
  selectedFiles: preSelectedFiles = [],
  onRunSelectedFiles,
  onFilesChanged,
  files = [],
}: FileUploadFormProps): ReactElement => {
  const { selectedProject } = useProjectStore();
  const { auth } = useAuth();

  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );
  const [editingFile, setEditingFile] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState("");
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadRole, setUploadRole] = useState("auto");
  const [expressionOrientation, setExpressionOrientation] = useState("genes_by_cells");
  const [barcodeColumn, setBarcodeColumn] = useState("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(e.target.files);
      setUploadProgress({});
      setUploadStatus("");
      // Immediately upload picked files
      void handleUpload(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      setSelectedFiles(e.dataTransfer.files);
      setUploadProgress({});
      setUploadStatus("");
      // Immediately upload dropped files
      void handleUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleUpload = async (filesToUpload?: FileList | null) => {
    const filesParam = filesToUpload ?? selectedFiles;
    if (!filesParam || filesParam.length === 0) {
      setUploadStatus("No files selected");
      return;
    }
    if (!auth?.accessToken || !selectedProject) {
      setUploadStatus("Authentication required");
      return;
    }

    setUploadStatus("Uploading files...");

    try {
      await projectsService.uploadProjectFiles(
        auth,
        selectedProject.project_name,
        filesParam,
        uploadRole,
        uploadRole === "expression_matrix"
          ? { expression_orientation: expressionOrientation }
          : uploadRole === "metadata" && barcodeColumn.trim()
            ? { barcode_column: barcodeColumn.trim() }
            : {},
      );
      setUploadStatus("Files uploaded successfully");

      // Extract file names for parent component
      const uploadedFileNames = Array.from(filesParam).map((file) => file.name);

      setSelectedFiles(null);
      onSubmit?.(uploadedFileNames); // Notify parent component with file names
      // Don't close modal - let user see uploaded files and run them
    } catch (error) {
      setUploadStatus("Upload failed");
    }
  };

  const handleRename = async (fileId: string) => {
    if (!auth?.accessToken || !selectedProject) return;

    try {
      await projectsService.renameProjectFile(
        auth,
        selectedProject.project_name,
        fileId,
        newFileName,
      );
      setEditingFile(null);
      setNewFileName("");
      onFilesChanged?.(); // Notify parent component to refresh files
    } catch (error) {
      setUploadStatus("Failed to rename file");
    }
  };

  const handleDelete = (fileId: string) => {
    setFileToDelete(fileId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete || !auth?.accessToken || !selectedProject) return;

    setIsDeleting(true);
    try {
      await projectsService.deleteProjectFile(
        auth,
        selectedProject.project_name,
        fileToDelete,
      );
      setSelectedFileIds((prev) => prev.filter((id) => id !== fileToDelete));
      setShowDeleteModal(false);
      setFileToDelete(null);
      onFilesChanged?.(); // Notify parent component to refresh files
    } catch (error) {
      setUploadStatus("Failed to delete file");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedFileIds(checked ? files.map((f) => f.id) : []);
  };

  const handleRowSelect = (fileId: string, checked: boolean) => {
    setSelectedFileIds((prev) =>
      checked
        ? [...new Set([...prev, fileId])]
        : prev.filter((id) => id !== fileId),
    );
  };

  const handleRunSelectedFiles = () => {
    if (selectedFileIds.length === 0) return;

    // Get selected file names
    const selectedFileNames = files
      .filter((file) => selectedFileIds.includes(file.id))
      .map((file) => file.name);

    // Notify parent with selected files for running
    onRunSelectedFiles?.(selectedFileNames);

    // Close modal after running selected files
    onCancel?.();
  };

  const handleDownload = (fileId: string) => {
    // TODO: Implement file download
    // console.log('Download file:', fileId);
  };

  // Sync pre-selected files with internal state
  useEffect(() => {
    if (preSelectedFiles.length > 0 && files.length > 0) {
      const preSelectedIds = files
        .filter((file) => preSelectedFiles.includes(file.name))
        .map((file) => file.id);
      setSelectedFileIds(preSelectedIds);
    }
  }, [preSelectedFiles, files]);

  const paginatedFiles = files.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const allSelected =
    files.length > 0 && selectedFileIds.length === files.length;
  const partiallySelected =
    selectedFileIds.length > 0 && selectedFileIds.length < files.length;

  return (
    <div className="w-full max-h-[70vh] overflow-auto mt-2">
      {/* Upload Section */}
      <div className="mb-6">
        <FormControl fullWidth size="small" className="mb-3">
          <InputLabel id="upload-role-label">Import as</InputLabel>
          <Select
            labelId="upload-role-label"
            value={uploadRole}
            label="Import as"
            onChange={(event) => setUploadRole(event.target.value)}
          >
            <MenuItem value="auto">Detect automatically / 10x files</MenuItem>
            <MenuItem value="matrix">10x role: Matrix (.mtx)</MenuItem>
            <MenuItem value="genes">10x role: Features / genes (.tsv)</MenuItem>
            <MenuItem value="barcodes">10x role: Cell barcodes (.tsv)</MenuItem>
            <MenuItem value="expression_matrix">Expression CSV/TSV</MenuItem>
            <MenuItem value="metadata">Cell metadata CSV/TSV</MenuItem>
            <MenuItem value="processed_data">Processed AnnData (.h5ad)</MenuItem>
          </Select>
        </FormControl>
        <Paper variant="outlined" sx={{ mb: 1.5, p: 1, backgroundColor: "#f7fbff", borderColor: "#9bb8d3" }}>
          <details>
            <summary style={{ cursor: "pointer", color: "#1565c0", fontSize: 13, fontWeight: 700 }}>
              Accepted formats, filenames, and gene identifiers
            </summary>
            <Stack spacing={0.75} sx={{ pt: 1 }}>
              <Typography variant="body2"><strong>10x MTX:</strong> upload one matrix.mtx(.gz), one features.tsv(.gz) or genes.tsv(.gz), and one barcodes.tsv(.gz). Upload all three from the same dataset.</Typography>
              <Typography variant="body2"><strong>Different filenames:</strong> upload each file separately and choose its explicit 10x role above. SurvOm will map it internally; renaming is not required.</Typography>
              <Typography variant="body2"><strong>Gene identifiers:</strong> column 1 is the gene ID and column 2 should be the gene symbol. If column 2 is absent, SurvOm copies column 1 and allows the upload to continue.</Typography>
              <Typography variant="body2"><strong>Ensembl-only data:</strong> copying column 1 does not convert Ensembl IDs to symbols, so marker and annotation matching may be limited. For best results, add standard symbols in column 2 or upload an .h5ad whose var_names are gene symbols.</Typography>
              <Typography variant="body2"><strong>Other formats:</strong> 10x filtered_feature_bc_matrix.h5, AnnData .h5ad, or expression CSV/TSV with the correct row/column layout.</Typography>
            </Stack>
          </details>
        </Paper>
        {(["matrix", "genes", "barcodes"].includes(uploadRole)) && (
          <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: "#7c2d12" }}>
            Explicit 10x role selected: upload only that one file, then repeat for the other two roles.
          </Typography>
        )}
        {uploadRole === "expression_matrix" && (
          <FormControl fullWidth size="small" className="mb-3">
            <InputLabel id="expression-orientation-label">Expression layout</InputLabel>
            <Select
              labelId="expression-orientation-label"
              value={expressionOrientation}
              label="Expression layout"
              onChange={(event) => setExpressionOrientation(event.target.value)}
            >
              <MenuItem value="genes_by_cells">Rows are genes, columns are cells</MenuItem>
              <MenuItem value="cells_by_genes">Rows are cells, columns are genes</MenuItem>
            </Select>
          </FormControl>
        )}
        {uploadRole === "metadata" && (
          <TextField
            fullWidth
            size="small"
            className="mb-3"
            label="Barcode column (defaults to first column)"
            value={barcodeColumn}
            onChange={(event) => setBarcodeColumn(event.target.value)}
            helperText="This column is matched to AnnData cell barcodes; row order is never used."
          />
        )}
        <div
          className="border-2 border-dashed border-[#19C1DB] rounded-lg p-6 text-center bg-gray-50 cursor-pointer hover:bg-blue-50 transition-colors"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => document.getElementById("file-input")?.click()}
        >
          <CloudUploadIcon
            className="text-[#19C1DB] mb-4"
            sx={{ fontSize: 48 }}
          />
          <Typography variant="h6" className="mb-2 text-[#19C1DB]">
            Drag and drop files here, or click to select
          </Typography>
          <Typography variant="body2" className="text-gray-600">
            Support for single-cell RNA sequencing data, bulk RNA-seq, and other
            multi-omics datasets
          </Typography>
          <input
            id="file-input"
            type="file"
            multiple
            accept=".mtx,.gz,.tsv,.csv,.txt,.h5,.h5ad"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {selectedFiles && selectedFiles.length > 0 && (
          <div className="mt-4">
            <Typography variant="subtitle2" className="mb-2">
              Selected Files ({selectedFiles.length}):
            </Typography>
            {Array.from(selectedFiles).map((file, index) => (
              <div key={index} className="flex items-center mb-2">
                <Typography variant="body2" className="flex-grow">
                  {file.name}
                </Typography>
                {uploadProgress[file.name] !== undefined && (
                  <div className="w-24 ml-4">
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress[file.name]}
                    />
                    <Typography variant="caption" className="text-xs">
                      {uploadProgress[file.name]}%
                    </Typography>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {uploadStatus && (
          <Alert
            severity={
              uploadStatus.includes("successfully") ? "success" : "error"
            }
            className="mt-4"
          >
            {uploadStatus}
          </Alert>
        )}
      </div>

      {/* Files Table */}
      {files.length > 0 && (
        <div>
          <Divider className="mb-4" />
          <Typography
            variant="h6"
            className="mb-4 text-[#19C1DB] font-semibold"
          >
            Uploaded Files ({files.length})
          </Typography>

          <TableContainer component={Paper} className="rounded-lg shadow-sm">
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={partiallySelected}
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      inputProps={{ "aria-label": "select all files" }}
                    />
                  </TableCell>
                  <TableCell>File Name</TableCell>
                  <TableCell>Import Type</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedFiles.map((file) => (
                  <TableRow key={file.id} hover className="hover:bg-blue-50">
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedFileIds.includes(file.id)}
                        onChange={(e) =>
                          handleRowSelect(file.id, e.target.checked)
                        }
                        inputProps={{ "aria-label": `select ${file.name}` }}
                      />
                    </TableCell>
                    <TableCell>
                      {editingFile === file.id ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            value={newFileName}
                            onChange={(e) => setNewFileName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRename(file.id);
                              if (e.key === "Escape") setEditingFile(null);
                            }}
                            size="small"
                            variant="outlined"
                            fullWidth
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleRename(file.id)}
                          >
                            Save
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setEditingFile(null)}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          onDoubleClick={() => {
                            setEditingFile(file.id);
                            setNewFileName(file.name);
                          }}
                          sx={{ cursor: "pointer", fontWeight: 500 }}
                        >
                          {file.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={(file.file_role || "raw_data").replaceAll("_", " ")}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(file.id)}
                          title="Download"
                          sx={{ color: "#19C1DB" }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingFile(file.id);
                            setNewFileName(file.name);
                          }}
                          title="Rename"
                          sx={{ color: "#f59e0b" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(file.id)}
                          title="Delete"
                          sx={{ color: "#ef4444" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={files.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count}`
              }
            />
          </TableContainer>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between gap-4 mt-6">
        <Button
          variant="outlined"
          onClick={onCancel}
          className="border-[#19C1DB] text-[#19C1DB] hover:border-[#17B1CB] hover:bg-blue-50"
        >
          Close
        </Button>

        {files.length > 0 && (
          <Button
            variant="contained"
            onClick={handleRunSelectedFiles}
            disabled={selectedFileIds.length === 0}
            startIcon={<PlayArrowIcon />}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Run Selected Files ({selectedFileIds.length})
          </Button>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <BaseModal
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Delete File"
        maxWidth="sm"
      >
        <ConfirmationForm
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          confirmText="Yes, Delete File"
          title="You are about to delete this file"
          message="This file will be permanently removed from the project."
          confirmColor="error"
          loading={isDeleting}
        />
      </BaseModal>
    </div>
  );
};

export default FileUploadForm;
